/**
 * Custom hooks for customers content sourced from local YAML files.
 *
 * Content files:
 * - content/customers/*.yaml - Individual customer profiles
 * - content/site/customers-page/index.yaml - Customers page configuration
 *
 * To update content, edit the YAML files directly. Changes take effect on rebuild.
 */

import { useStaticQuery, graphql } from "gatsby"

/**
 * Internal hook that fetches all customers data from local YAML files.
 */
function useCustomersData() {
  return useStaticQuery(graphql`
    query CustomersContentQuery {
      allCustomersYaml(sort: { order: ASC }) {
        nodes {
          name
          slug
          logo
          website
          featured
          order
        }
      }
      customersPageYaml {
        caseStudiesHeading
        supersetCustomersHeading
        customersHeading
        supersetCustomersImage {
          url
          title
        }
        caseStudies {
          slug
          description
          thumbnail {
            url
            title
          }
          blogPostSlug
        }
      }
    }
  `)
}

/**
 * Get all customer profiles.
 *
 * @returns {Array} Array of customer objects: [{ name, slug, logo, website, featured, order }]
 */
function useAllCustomers() {
  const data = useCustomersData()
  return data.allCustomersYaml?.nodes || []
}

/**
 * Get featured customers (shown in "Highlighted Customers" section).
 *
 * @returns {Array} Array of featured customer objects
 */
function useFeaturedCustomers() {
  const customers = useAllCustomers()
  return customers.filter((c) => c.featured === true)
}

/**
 * Get customers page configuration.
 *
 * @returns {Object} Page config: { caseStudiesHeading, supersetCustomersHeading, customersHeading, supersetCustomersImage, caseStudies }
 */
function useCustomersPageConfig() {
  const data = useCustomersData()
  return data.customersPageYaml || {}
}

/**
 * Get all customers page content formatted for the CustomersSection component.
 * This provides data in a format compatible with the existing component.
 *
 * @returns {Object} {
 *   caseStudiesHeading,
 *   caseStudies: [{ id, description, thumbnail, blogPost }],
 *   supersetCustomersHeading,
 *   supersetCustomersImage,
 *   customersHeading,
 *   customers: [{ id, title, logo, website }]
 * }
 */
export function useCustomersSectionData() {
  const config = useCustomersPageConfig()
  const featuredCustomers = useFeaturedCustomers()

  // Transform case studies to match component format
  const caseStudies = (config.caseStudies || []).map((cs, index) => ({
    id: cs.slug || `case-study-${index}`,
    description: cs.description,
    thumbnail: cs.thumbnail,
    blogPost: {
      __typename: "LocalBlog",
      slug: cs.blogPostSlug,
    },
  }))

  // Transform customers to match component format
  const customers = featuredCustomers.map((c) => ({
    id: c.slug,
    title: c.name,
    logo: { url: c.logo },
    website: c.website,
  }))

  return {
    caseStudiesHeading: config.caseStudiesHeading || "",
    caseStudies,
    supersetCustomersHeading: config.supersetCustomersHeading || "",
    supersetCustomersImage: config.supersetCustomersImage,
    customersHeading: config.customersHeading || "",
    customers,
  }
}
